import {useFunnel} from '@tinct/web-funnel';
import {useGetCampaignById} from '@tinct/core-api';
import {CampaignCreationFunnelState} from '../../campaign-creation-funnel/campaign-creation-funnel';
import {LandingPageStudio} from '../../../containers/landing-page-studio/landing-page-studio';
import type {EditorStepState} from '../../../containers/landing-page-studio/landing-page-studio';
import {useGenerateAllPages} from '../../../hooks/use-generate-all-pages';
import {useAutoRegenerateOutdatedPages} from '../../../hooks/use-auto-regenerate-outdated-pages';

type Step6PreviewProps = {
  onEditorStateChange: (state: EditorStepState) => void;
};

export const Step6Preview = ({ onEditorStateChange }: Step6PreviewProps) => {
  const { state: { campaign } } = useFunnel<CampaignCreationFunnelState>();
  const campaignId = campaign.id;

  if (!campaignId) {
    return <div>No campaign ID found.</div>;
  }

  return (
    <Step6PreviewContent
      campaignId={campaignId}
      onEditorStateChange={onEditorStateChange}
    />
  );
};

type Step6PreviewContentProps = {
  campaignId: string;
  onEditorStateChange: (state: EditorStepState) => void;
};

/**
 * Exported for the management-side CampaignStudioPage, which reuses the exact
 * same personalize & preview surface outside the funnel.
 */
export const Step6PreviewContent = ({ campaignId, onEditorStateChange }: Step6PreviewContentProps) => {
  const { data: campaign } = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        name: null,
        // The studio page menu gates Preview on the campaign being live with
        // the snippet detected.
        status: null,
        snippetStatus: { fields: { status: null } },
        // The studio chat's "apply to all pages" and the settings panel flag pages while this
        // surface is up; the refetch they trigger is what starts the regeneration below.
        outdatedStatus: null,
        pagesToRegenerateCount: null,
        landingPageOriginType: null,
        landingPageVariationAxis: null,
        landingPage: {
          fields: {
            id: null,
            url: null,
            currentVersion: {
              fields: {
                id: null,
                htmlKeys: null,
                packagingStatus: null,
                packagedAt: null,
              },
            },
          },
        },
        samplePages: null,
        statistics: {
          fields: {
            generationJobsStatistics: null,
          }
        },
        blocks: {
          limit: 200,
        },
        // Page list only — each page's block variants are fetched on demand by
        // LandingPageStudio, so a campaign with hundreds of pages stays light.
        pages: {
          limit: 1000,
          fields: {
            id: null,
            lead: null,
            status: null,
            outdatedStatus: null,
            targetType: null,
            adCampaign: { fields: { id: null, name: null } },
            errorCode: null,
            lastGenerationJob: null,
          }
        }
      },
    },
    {
      enabled: !!campaignId,
      refetchInterval: (query) => {
        const stats = query.state.data?.statistics?.generationJobsStatistics;
        const activeCount = (stats?.runningCount ?? 0) + (stats?.queuedCount ?? 0);
        // Also while the current version's preview artifact is being
        // (re)packaged — `pending`/`packaging` — so the studio picks up the
        // new `packagedAt` and remounts the preview (preview-iframe contract,
        // rule 1). `packaged` and `failed` both stop it.
        const version = query.state.data?.landingPage?.currentVersion;
        const packagingInFlight =
          !!version?.id &&
          version.packagingStatus !== 'packaged' &&
          version.packagingStatus !== 'failed';
        return activeCount > 0 || packagingInFlight ? 3000 : false;
      },
    }
  );

  // Above the early return below so the hook order stays stable while loading.
  const { generateAll } = useGenerateAllPages(campaignId, campaign?.pages ?? []);
  useAutoRegenerateOutdatedPages(campaign);

  if (!campaign?.pages) {
    return (
      <div className="flex flex-1 w-full items-center justify-center py-24">
        <span className="text-muted-foreground">Loading preview...</span>
      </div>
    );
  }

  const handleGenerateAll = () => {
    void generateAll();
  };

  return (
    <LandingPageStudio
      key={campaign.id}
      campaign={campaign}
      onGenerateAll={handleGenerateAll}
      onEditorStateChange={onEditorStateChange}
    />
  );
};
